import React from 'react';
import {
    VStack,
    FormControl,
    FormLabel,
    Input,
    Select,
    Button,
    Text,
    Box,
    Divider,
    HStack,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    Flex,
    Icon,
} from '@chakra-ui/react';
import { MdCheckCircle, MdWarning } from 'react-icons/md';

export default function ABTestProperties({
    nodeConfig,
    setNodeConfig,
    updateNodeData,
    setSelectedNode
}) {
    return (
        <VStack spacing={4} align="stretch">
            <FormControl>
                <FormLabel fontSize="sm">Step Name</FormLabel>
                <Input
                    size="sm"
                    value={nodeConfig.label}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                    placeholder="e.g., A/B Test Campaign"
                />
            </FormControl>

            <Divider />

            <VStack spacing={4} align="stretch">
                {/* Number of Branches */}
                <FormControl>
                    <FormLabel fontSize="sm">Number of Branches</FormLabel>
                    <Select
                        size="sm"
                        value={nodeConfig.numBranches?.toString() || '2'}
                        onChange={(e) => {
                            const num = parseInt(e.target.value);
                            const equalPercentage = Math.floor(100 / num);
                            const branches = {};
                            for (let i = 0; i < num; i++) {
                                const label = String.fromCharCode(65 + i);
                                branches[label] = i === 0 ? (100 - equalPercentage * (num - 1)) : equalPercentage;
                            }
                            setNodeConfig({
                                ...nodeConfig,
                                numBranches: num,
                                branches
                            });
                        }}
                    >
                        <option value="2">2 (A/B)</option>
                        <option value="3">3 (A/B/C)</option>
                        <option value="4">4 (A/B/C/D)</option>
                        <option value="5">5 (A/B/C/D/E)</option>
                    </Select>
                </FormControl>

                <Divider />

                <Box>
                    <FormLabel fontSize="sm" mb={3}>Traffic Split Configuration</FormLabel>

                    {/* Dynamic Variants with Sliders */}
                    <VStack spacing={3} align="stretch">
                        {Array.from({ length: nodeConfig.numBranches || 2 }, (_, i) => {
                            const label = String.fromCharCode(65 + i);
                            const percentage = nodeConfig.branches?.[label] || Math.floor(100 / (nodeConfig.numBranches || 2));

                            return (
                                <Box
                                    key={label}
                                    p={3}
                                    bg="blue.50"
                                    borderWidth="1px"
                                    borderColor="blue.200"
                                    borderRadius="lg"
                                >
                                    <Flex justify="space-between" mb={2}>
                                        <Text fontSize="sm" fontWeight="semibold" color="blue.900">Variant {label}</Text>
                                        <Text fontSize="sm" fontWeight="semibold" color="blue.900">{percentage}%</Text>
                                    </Flex>
                                    <Slider
                                        value={percentage}
                                        onChange={(value) => {
                                            const newBranches = { ...nodeConfig.branches };
                                            newBranches[label] = value; // Only change the current variant
                                            setNodeConfig({
                                                ...nodeConfig,
                                                branches: newBranches
                                            });
                                        }}
                                        min={0}
                                        max={100}
                                    >
                                        <SliderTrack bg="white">
                                            <SliderFilledTrack bg="var(--brand-primary)" />
                                        </SliderTrack>
                                        <SliderThumb />
                                    </Slider>

                                    <Box h="8px" bg="white" borderRadius="full" overflow="hidden" borderWidth="1px" borderColor="blue.300" mt={2}>
                                        <Box
                                            h="full"
                                            bg="var(--brand-primary)"
                                            transition="all 0.3s"
                                            w={`${percentage}%`}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}

                        {/* Traffic Split Validation Indicator */}
                        {(() => {
                            const total = Object.values(nodeConfig.branches || {}).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
                            const isValid = total === 100;


                            return (
                                <Box
                                    p={3}
                                    borderRadius="lg"
                                    borderWidth="1px"
                                    bg={isValid ? 'green.50' : 'red.50'}
                                    borderColor={isValid ? 'green.200' : 'red.200'}
                                >
                                    <Flex justify="space-between" align="center">
                                        <HStack>
                                            <Icon as={isValid ? MdCheckCircle : MdWarning} color={isValid ? 'green.600' : 'red.600'} />
                                            <Text
                                                fontSize="sm"
                                                fontWeight="medium"
                                                color={isValid ? 'green.900' : 'red.900'}
                                            >
                                                Total: {total}%
                                            </Text>
                                        </HStack>
                                        {!isValid && (
                                            <Text fontSize="xs" color="red.700">Must equal 100%</Text>
                                        )}
                                    </Flex>
                                </Box>
                            );
                        })()}
                    </VStack>
                </Box>

                <Box p={3} bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="lg">
                    <HStack align="start" spacing={2}>
                        <Icon as={MdWarning} color="orange.600" mt={0.5} flexShrink={0} />
                        <VStack align="start" spacing={1} fontSize="xs" color="orange.800">
                            <Text fontWeight="medium">How A/B Testing Works</Text>
                            <Text>Traffic will be randomly split between variants based on the percentages above. Connect different message nodes to each output to test different approaches.</Text>
                            <Text color="orange.700">
                                <strong>Tip:</strong> Adjust the sliders to distribute traffic. The total must equal exactly 100% before you can update the node.
                            </Text>
                        </VStack>
                    </HStack>
                </Box>
            </VStack>

            <Divider />

            <HStack>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedNode && setSelectedNode(null)}
                >
                    Cancel
                </Button>
                <Button
                    size="sm"
                    colorScheme="blue"
                    bg="var(--brand-primary)"
                    _hover={{ bg: 'var(--brand-primary-hover)' }}
                    onClick={updateNodeData}
                    flex={1}
                    isDisabled={(() => {
                        const total = Object.values(nodeConfig.branches || {}).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
                        return total !== 100;
                    })()}
                >
                    {(() => {
                        const total = Object.values(nodeConfig.branches || {}).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
                        return total !== 100 ? 'Adjust to 100% to Update' : 'Update Node';
                    })()}
                </Button>
            </HStack>
        </VStack>
    );
}
